import React, { useState, useEffect, useCallback, useRef } from 'react';
import client from '../../api/client';
import { uploadFile } from '../../utils/upload';
import { Plus, Edit3, Trash2, Save, FolderTree, ChevronUp, ChevronDown, Check, ArrowLeft, Upload, Image as ImageIcon } from 'lucide-react';
import { toast, confirmDialog } from '../../components/Toast/toast';

export default function CategoriesAdmin({ embedded = false }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', image_url: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [animating, setAnimating] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    client.get('/catalog/categories')
      .then(res => setCategories(res.data || []))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Сохранение нового порядка
  const saveOrder = async (reordered) => {
    try {
      await Promise.all(
        reordered.map((cat, index) =>
          client.put(`/admin/categories/${cat.id}`, { sort_order: index })
        )
      );
    } catch (err) {
      console.error('Ошибка сохранения порядка:', err);
    }
  };

  // Перемещение категории вверх/вниз с анимацией
  const moveCategory = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= categories.length) return;
    if (animating) return;

    // Запускаем анимацию сдвига
    setAnimating({ movedId: categories[index].id, targetId: categories[targetIndex].id, direction });

    // После анимации — реальная перестановка
    setTimeout(() => {
      setCategories(prev => {
        const arr = [...prev];
        const fromIdx = arr.findIndex(c => c.id === categories[index].id);
        const toIdx = arr.findIndex(c => c.id === categories[targetIndex].id);
        [arr[fromIdx], arr[toIdx]] = [arr[toIdx], arr[fromIdx]];
        saveOrder(arr);
        return arr;
      });
      setAnimating(null);
    }, 300);
  };

  const openAdd = () => {
    setEditing('new');
    setForm({ name: '', slug: '', image_url: '', sort_order: 0 });
    setUploadError('');
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ name: c.name || '', slug: c.slug || '', image_url: c.image_url || '', sort_order: c.sort_order ?? 0 });
    setUploadError('');
  };

  const closeForm = () => {
    setEditing(null);
    setForm({ name: '', slug: '', image_url: '', sort_order: 0 });
    setUploadError('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const url = await uploadFile(file);
      setForm(prev => ({ ...prev, image_url: url }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Не удалось загрузить фото';
      setUploadError(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.slug.trim()) return;
    setSaving(true);
    setSaved(false);
    try {
      const payload = {
        name: form.name,
        slug: form.slug,
        image_url: form.image_url || null,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editing === 'new') {
        await client.post('/admin/categories', payload);
      } else {
        await client.put(`/admin/categories/${editing}`, payload);
      }
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        closeForm();
        load();
      }, 1200);
    } catch (err) {
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog('Удалить категорию?', {
      danger: true,
      okLabel: 'Удалить',
      cancelLabel: 'Отмена',
      detail: 'Товары в этой категории будут переведены в «Без категории».',
      onConfirm: async () => {
        try {
          await client.delete(`/admin/categories/${id}`);
          load();
        } catch (err) {
          toast.error('Ошибка удаления');
          throw err;
        }
      },
    });
    if (!confirmed) return;
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[а-яё]/g, c => ({'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ё':'e','ж':'zh','з':'z','и':'i','й':'y','к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u','ф':'f','х':'h','ц':'ts','ч':'ch','ш':'sh','щ':'shch','ъ':'','ы':'y','ь':'','э':'e','ю':'yu','я':'ya'}[c] || c))
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const content = editing !== null ? (
    /* Редактор категории — отдельной страницей (как «Новый товар»):
       список не показываем, чтобы форма не уезжала вниз и была сразу видна */
    <div className="space-y-4">
      <header className="rounded-2xl border border-[#e2e8f0] bg-white px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={closeForm} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#f1f5f9] text-[#64748b] transition active:scale-90">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="min-w-0 flex-1 truncate text-sm font-bold text-[#0f172a]">
            {editing === 'new' ? 'Новая категория' : 'Редактирование категории'}
          </h2>
        </div>
      </header>
      <div className="rounded-2xl border border-[#e2e8f0] bg-white p-4">
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-[#64748b]">Название *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => {
                const name = e.target.value;
                setForm(prev => ({ ...prev, name, slug: generateSlug(name) }));
              }}
              placeholder="Плодовые деревья"
              className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[#64748b]">Фото категории</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            <div className="flex items-center gap-3">
              {form.image_url ? (
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-[#e2e8f0]">
                  <img src={form.image_url} alt="preview" className="h-full w-full object-cover" />
                </div>
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-[#e2e8f0] text-[#e2e8f0]">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-2 text-xs font-medium text-[#64748b] transition hover:bg-[#f8fafc] active:scale-95 disabled:opacity-50"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {uploading ? 'Загрузка...' : 'Выбрать фото'}
                </button>
                {form.image_url && (
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, image_url: '' }))}
                    className="text-xs text-[#D64545]/70 hover:text-[#D64545]"
                  >
                    Удалить фото
                  </button>
                )}
              </div>
            </div>
            {uploadError && (
              <p className="mt-1 text-xs text-[#D64545]">{uploadError}</p>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={closeForm} className="flex-1 rounded-xl border border-[#e2e8f0] py-2.5 text-sm font-medium text-[#64748b] hover:bg-[#f8fafc]">
              Отмена
            </button>
            <button
              onClick={handleSave}
              disabled={saving || saved || !form.name.trim() || !form.slug.trim()}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-semibold transition active:scale-[0.98] disabled:opacity-50 ${
                saved
                  ? 'bg-[#6B9B74]/15 text-[#6B9B74]'
                  : 'bg-[#374151] text-white hover:bg-[#1f2937]'
              }`}
            >
              {saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
              {saved ? 'Сохранено!' : saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-[#64748b]">
          {categories.length} {categories.length === 1 ? 'категория' : 'категорий'}
        </h2>
        <button
          onClick={openAdd}
          className="flex shrink-0 items-center gap-1.5 rounded-xl bg-[#374151] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1f2937] active:scale-[0.97]"
        >
          <Plus className="h-4 w-4" />
          Добавить
        </button>
      </div>

      {loading ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">Загрузка...</div>
      ) : categories.length === 0 ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">
          <FolderTree className="mx-auto mb-2 h-10 w-10 text-[#e2e8f0]" />
          <p>Категории не добавлены</p>
        </div>
      ) : (
        <div className="space-y-2">
          {categories.map((c, index) => {
            // Определяем сдвиг для анимации
            let transform = 'translateY(0)';
            if (animating) {
              if (animating.movedId === c.id) {
                // Элемент, который двигаем
                transform = animating.direction === -1 ? 'translateY(-100%)' : 'translateY(100%)';
              } else if (animating.targetId === c.id) {
                // Сосед, который сдвигается
                transform = animating.direction === -1 ? 'translateY(100%)' : 'translateY(-100%)';
              }
            }
            const isMoving = animating && (animating.movedId === c.id || animating.targetId === c.id);

            return (
              <div
                key={c.id}
                style={{
                  transform: isMoving ? transform : 'translateY(0)',
                  transition: isMoving ? 'transform 300ms ease-in-out' : 'none',
                  position: 'relative',
                  zIndex: isMoving ? 1 : 0,
                }}
                className="flex items-center gap-3 rounded-xl border border-[#e2e8f0] bg-white p-3 shadow-sm"
              >
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => moveCategory(index, -1)}
                  disabled={index === 0}
                  className="grid h-5 w-6 place-items-center rounded text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-[#64748b]"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  onClick={() => moveCategory(index, 1)}
                  disabled={index === categories.length - 1}
                  className="grid h-5 w-6 place-items-center rounded text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a] disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-[#64748b]"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
              {c.image_url && (
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-[#f1f5f9]">
                  <img src={c.image_url} alt="" className="h-full w-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0f172a]">{c.name}</div>
              </div>
              <div className="flex shrink-0 gap-1">
                <button onClick={() => openEdit(c)} className="grid h-9 w-9 place-items-center rounded-lg text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a]">
                  <Edit3 className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(c.id)} className="grid h-9 w-9 place-items-center rounded-lg text-[#64748b] hover:bg-[#D64545]/10 hover:text-[#D64545]">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (embedded) return content;
  return <div className="px-4 py-4">{content}</div>;
}
